/**
 * Support Ticket Form Handler
 * Handles form submission to the Snaps API
 * Matches order form style and functionality
 */

class SupportTicketForm {
  constructor() {
    this.form = document.getElementById('SupportTicketForm');
    this.submitButton = document.getElementById('submit-button');
    this.successMessage = document.getElementById('ticket-success');
    this.errorMessage = document.getElementById('ticket-error');
    this.errorMessageText = document.getElementById('ticket-error-message');
    
    // API Configuration
    this.apiUrl = 'https://api.bysnaps.ai/tickets';
    this.bearerToken = 'snaps_internal_api_token_2024';
    
    this.init();
  }

  init() {
    if (!this.form) return;

    this.form.addEventListener('submit', (e) => {
      e.preventDefault();
      this.handleSubmit();
    });

    // Real-time validation
    const inputs = this.form.querySelectorAll('.form-input, .form-textarea, .form-select');
    inputs.forEach(input => {
      input.addEventListener('blur', () => {
        this.validateField(input);
      });
      
      // Clear error on input
      input.addEventListener('input', () => {
        this.clearFieldError(input);
      });

      // Clear error on change for selects
      input.addEventListener('change', () => {
        this.clearFieldError(input);
      });
    });
  }

  validateField(field) {
    const errorElement = document.getElementById(`${field.id}-error`);
    if (!errorElement) return true;

    let isValid = true;
    let errorMessage = '';

    // Check if required
    if (field.hasAttribute('required') && !field.value.trim()) {
      isValid = false;
      errorMessage = 'This field is required';
    }

    // Email validation (only if not empty, since it's optional)
    if (field.type === 'email' && field.value.trim()) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(field.value)) {
        isValid = false;
        errorMessage = 'Please enter a valid email address';
      }
    }

    // Phone validation
    if (field.type === 'tel' && field.value.trim()) {
      // Basic phone validation - must start with + or digit and contain numbers
      const phoneRegex = /^[\+]?[\d\s\-()]+$/;
      if (!phoneRegex.test(field.value) || field.value.replace(/\D/g, '').length < 8) {
        isValid = false;
        errorMessage = 'Please enter a valid phone number';
      }
    }

    // Select validation
    if (field.tagName === 'SELECT' && field.hasAttribute('required') && !field.value) {
      isValid = false;
      errorMessage = 'Please select an option';
    }

    if (!isValid) {
      field.classList.add('error');
      errorElement.textContent = errorMessage;
      errorElement.style.display = 'block';
    } else {
      this.clearFieldError(field);
    }

    return isValid;
  }

  clearFieldError(field) {
    const errorElement = document.getElementById(`${field.id}-error`);
    if (errorElement) {
      field.classList.remove('error');
      errorElement.textContent = '';
      errorElement.style.display = 'none';
    }
  }

  validateForm() {
    const requiredFields = this.form.querySelectorAll('[required]');
    let isValid = true;
    let firstInvalidField = null;

    requiredFields.forEach(field => {
      if (!this.validateField(field)) {
        isValid = false;
        if (!firstInvalidField) {
          firstInvalidField = field;
        }
      }
    });

    // Focus on first invalid field
    if (firstInvalidField) {
      firstInvalidField.focus();
      firstInvalidField.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    return isValid;
  }

  getFormData() {
    const formData = new FormData(this.form);
    
    const data = {
      title: formData.get('title'),
      description: formData.get('description'),
      customer: {
        name: formData.get('customer_name'),
        phone: formData.get('customer_phone')
      },
      source: formData.get('source'),
      category: formData.get('category'),
      type: formData.get('type')
    };

    // Only include email if provided
    const email = formData.get('customer_email');
    if (email && email.trim()) {
      data.customer.email = email;
    }

    return data;
  }

  setLoading(isLoading) {
    if (isLoading) {
      this.submitButton.disabled = true;
    } else {
      this.submitButton.disabled = false;
    }
  }

  showSuccess() {
    this.successMessage.style.display = 'block';
    this.errorMessage.style.display = 'none';
    this.form.style.display = 'none';
    
    // Scroll to success message
    this.successMessage.scrollIntoView({ behavior: 'smooth', block: 'center' });
    
    // Focus for accessibility
    this.successMessage.setAttribute('tabindex', '-1');
    this.successMessage.focus();

    // Optional: Reset form after a delay
    setTimeout(() => {
      this.form.reset();
    }, 500);
  }

  showError(message) {
    this.errorMessage.style.display = 'block';
    this.successMessage.style.display = 'none';
    this.errorMessageText.textContent = message;
    
    // Scroll to error message
    this.errorMessage.scrollIntoView({ behavior: 'smooth', block: 'center' });
    
    // Focus for accessibility
    this.errorMessage.setAttribute('tabindex', '-1');
    this.errorMessage.focus();
  }

  hideMessages() {
    this.successMessage.style.display = 'none';
    this.errorMessage.style.display = 'none';
  }

  async handleSubmit() {
    // Hide previous messages
    this.hideMessages();

    // Validate form
    if (!this.validateForm()) {
      this.showError('Please fix the errors in the form before submitting.');
      return;
    }

    // Get form data
    const ticketData = this.getFormData();

    // Set loading state
    this.setLoading(true);

    try {
      const response = await fetch(this.apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.bearerToken}`
        },
        body: JSON.stringify(ticketData)
      });

      let responseData;
      try {
        responseData = await response.json();
      } catch (e) {
        // If response is not JSON
        responseData = {};
      }

      if (response.ok) {
        // Success
        this.showSuccess();
      } else {
        // API returned an error
        const errorMsg = responseData.message || responseData.error || `Server error (${response.status}). Please try again.`;
        this.showError(errorMsg);
      }
    } catch (error) {
      console.error('Error submitting ticket:', error);
      
      // Check if it's a network error
      if (error.message === 'Failed to fetch' || error.name === 'TypeError') {
        this.showError('Network error. Please check your connection and try again.');
      } else {
        this.showError('An unexpected error occurred. Please try again.');
      }
    } finally {
      this.setLoading(false);
    }
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new SupportTicketForm();
  });
} else {
  new SupportTicketForm();
}

// Re-initialize on Shopify section updates (for theme editor)
if (typeof Shopify !== 'undefined' && Shopify.designMode) {
  document.addEventListener('shopify:section:load', () => {
    new SupportTicketForm();
  });
}
